import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import { Close, FilterList, Inbox } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bulkUpdateFocalPoint, getAll } from "../../../../services/actions.service";
import { getMembersByName } from "../../../../services/formCombos.service";
import { Select } from "../../../Common/Select";
import { actionsStatusOptions } from "../../utils/index";
import {
  ActionButtons,
  ActionsTable,
  CloseButton,
  Container,
  EmptyState,
  FilterSection,
  FocalPointSection,
  Header,
  InitialState,
  LoadingContainer,
  SelectionSummary,
  TableHeader,
  TableRow,
  TitleSection,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
  filterButton: {
    fontSize: "0.875rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 120,
    minHeight: 40,

    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      cursor: 'pointer',
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey.light,
      color: `${theme.palette.primary.contrastText} !important`,
    },
  },
  chip: {
    margin: 2,
  },
}));

function UpdateMultipleFocalPointModal({ onClose, onSuccess }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [actions, setActions] = useState([]);
  const [selectedActionIds, setSelectedActionIds] = useState([]);

  // Filter states
  const [responsibleInputValue, setResponsibleInputValue] = useState("");
  const [responsibles, setResponsibles] = useState([]);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [actionStatus, setActionStatus] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // New focal point states
  const [focalPointInputValue, setFocalPointInputValue] = useState("");
  const [focalPoints, setFocalPoints] = useState([]);
  const [selectedFocalPoint, setSelectedFocalPoint] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const options = actionsStatusOptions(localStorage.getItem("i18nextLng")) || [];
    setStatusOptions(options);
  }, []);

  useEffect(() => {
    async function loadResponsibles() {
      if (responsibleInputValue?.length >= 3) {
        const res = await getMembersByName("pt", responsibleInputValue);

        if (res.error?.length > 0) {
          setResponsibles([]);
        } else {
          setResponsibles(res.data || []);
        }
      } else {
        setResponsibles([]);
      }
    }

    loadResponsibles();
  }, [responsibleInputValue]);

  useEffect(() => {
    async function loadFocalPoints() {
      if (focalPointInputValue?.length >= 3) {
        const res = await getMembersByName("pt", focalPointInputValue);

        if (res.error?.length > 0) {
          setFocalPoints([]);
        } else {
          setFocalPoints(res.data || []);
        }
      } else {
        setFocalPoints([]);
      }
    }

    loadFocalPoints();
  }, [focalPointInputValue]);

  const handleSearch = async () => {
    setLoading(true);
    setHasSearched(true);
    setError("");

    try {
      const response = await getAll({
        ...(actionStatus.length > 0 && {
          actionStatus: actionStatus.map((status) => status.id),
        }),
        ...(selectedResponsible && {
          responsibleId: selectedResponsible.id,
        }),
        pageSize: 1000000,
        currentPage: 0,
      });

      const actionsList = response.items || [];
      setActions(actionsList);
      setSelectedActionIds(actionsList.map((action) => action.id));
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAction = (actionId) => {
    setSelectedActionIds((prev) => {
      if (prev.includes(actionId)) {
        return prev.filter((id) => id !== actionId);
      }
      return [...prev, actionId];
    });
  };

  const handleToggleAll = () => {
    if (selectedActionIds.length === actions.length) {
      setSelectedActionIds([]);
    } else {
      setSelectedActionIds(actions.map((action) => action.id));
    }
  };

  const handleSubmit = async () => {
    setError("");

    if (selectedActionIds.length === 0) {
      setError(t("menu.actions.bulkFocalPoint.noActionsSelected"));
      return;
    }

    if (!selectedFocalPoint) {
      setError(t("menu.actions.bulkFocalPoint.focalPointRequired"));
      return;
    }

    setSubmitting(true);

    try {
      const response = await bulkUpdateFocalPoint({
        focalPointId: selectedFocalPoint.id,
        actionIds: selectedActionIds,
      });

      if (response.error) {
        setError(response.error);
        setSubmitting(false);
        return;
      }

      onSuccess();
    } catch {
      setError("async");
      setSubmitting(false);
    }
  };

  const isAllSelected = actions.length > 0 && selectedActionIds.length === actions.length;
  const isIndeterminate = selectedActionIds.length > 0 && selectedActionIds.length < actions.length;
  const canSearch = selectedResponsible || actionStatus.length > 0;

  return (
    <Container>
      <Header>
        <TitleSection>
          <h2>{t("menu.actions.bulkFocalPoint.title")}</h2>
          <p>{t("menu.actions.bulkFocalPoint.subtitle")}</p>
        </TitleSection>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
      </Header>

      <FilterSection>
        <div className="filter-field">
          <Select
            fullWidth
            id="responsible"
            options={responsibles}
            value={selectedResponsible}
            onInputChange={(_event, newInputValue, _prop) => {
              setResponsibleInputValue(newInputValue);
            }}
            inputValue={responsibleInputValue}
            label={t("forms.minutes.actions.responsible")}
            setValue={(value) => {
              setSelectedResponsible(value);
            }}
          />
        </div>

        <div className="filter-field">
          <Select
            fullWidth
            id="status"
            options={statusOptions}
            value={actionStatus}
            label={t("menu.actions.status")}
            setValue={(values) => {
              setActionStatus(values);
            }}
            multiple={true}
            renderTags={(selectedValues) => {
              return selectedValues.map((option) => (
                <Chip
                  className={classes.chip}
                  key={option.id}
                  onDelete={() => {
                    setActionStatus(
                      actionStatus.filter((v) => v.id !== option.id)
                    );
                  }}
                  onClick={() => {
                    setActionStatus(
                      actionStatus.filter((v) => v.id !== option.id)
                    );
                  }}
                  variant="outlined"
                  label={option.name}
                />
              ));
            }}
          />
        </div>

        <div className="filter-actions">
          <Button
            className={classes.filterButton}
            startIcon={<FilterList />}
            onClick={handleSearch}
            disabled={loading || !canSearch}
          >
            {t("menu.actions.bulkFocalPoint.search")}
          </Button>
        </div>
      </FilterSection>

      {loading ? (
        <LoadingContainer>
          <CircularProgress />
          <p>{t("menu.actions.bulkFocalPoint.loading")}</p>
        </LoadingContainer>
      ) : !hasSearched ? (
        <InitialState>
          <FilterList />
          <p>{t("menu.actions.bulkFocalPoint.initialMessage")}</p>
          <span className="hint">{t("menu.actions.bulkFocalPoint.initialHint")}</span>
        </InitialState>
      ) : actions.length === 0 ? (
        <EmptyState>
          <Inbox />
          <p>{t("menu.actions.bulkFocalPoint.noActions")}</p>
        </EmptyState>
      ) : (
        <>
          <SelectionSummary>
            <span className="count">
              <strong>{selectedActionIds.length}</strong> / {actions.length}{" "}
              {t("menu.actions.bulkFocalPoint.selectedCount")}
            </span>
            <button
              className="select-all-btn"
              onClick={handleToggleAll}
            >
              {isAllSelected
                ? t("menu.actions.bulkFocalPoint.deselectAll")
                : t("menu.actions.bulkFocalPoint.selectAll")}
            </button>
          </SelectionSummary>

          <ActionsTable>
            <TableHeader>
              <div>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleToggleAll}
                />
              </div>
              <div>#</div>
              <div>{t("menu.slot.link.parent")}</div>
              <div>{t("forms.minutes.actions.action")}</div>
              <div>{t("forms.minutes.actions.responsible")}</div>
              <div>{t("forms.minutes.actions.focalPoint")}</div>
            </TableHeader>

            {actions.map((action, index) => (
              <TableRow
                key={action.id}
                $index={index}
                $isSelected={selectedActionIds.includes(action.id)}
                $hasFocalPoint={!!action.focalPoint}
              >
                <div>
                  <Checkbox
                    checked={selectedActionIds.includes(action.id)}
                    onChange={() => handleToggleAction(action.id)}
                  />
                </div>
                <div>
                  <span className="action-number">{action.actionNumber}</span>
                </div>
                <div>
                  <span className="theme-text" title={action.theme}>
                    {action.theme || "-"}
                  </span>
                </div>
                <div>
                  <span className="description-text" title={action.actionDescription}>
                    {action.actionDescription || "-"}
                  </span>
                </div>
                <div>
                  <span className="responsible-text" title={action.firstResponsible}>
                    {action.firstResponsible || "-"}
                  </span>
                </div>
                <div>
                  <span className="focal-point-text" title={action.focalMemberName}>
                    {action.focalMemberName || "-"}
                  </span>
                </div>
              </TableRow>
            ))}
          </ActionsTable>

          <FocalPointSection>
            <div className="select-wrapper">
              <Select
                fullWidth
                id="focalPoint"
                options={focalPoints}
                value={selectedFocalPoint}
                onInputChange={(_event, newInputValue, _prop) => {
                  setFocalPointInputValue(newInputValue);
                }}
                inputValue={focalPointInputValue}
                label={t("menu.actions.bulkFocalPoint.newFocalPoint")}
                setValue={(value) => {
                  setSelectedFocalPoint(value);
                }}
              />
            </div>
          </FocalPointSection>
        </>
      )}

      {error && (
        <p style={{ color: "#E11021", marginTop: 16, fontSize: "0.875rem" }}>
          {error}
        </p>
      )}

      <ActionButtons>
        <Button
          variant="contained"
          onClick={onClose}
          className={classes.buttonOutlined}
          disabled={submitting}
        >
          {t("forms.committee.buttons.cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          className={classes.button}
          disabled={submitting || actions.length === 0}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("menu.actions.bulkFocalPoint.save")
          )}
        </Button>
      </ActionButtons>
    </Container>
  );
}

export default UpdateMultipleFocalPointModal;
